/**
 * Eventos que contam como "a pessoa começou a usar a página". `scroll` e
 * `wheel` entram porque rolar é o gesto mais comum numa página de vitrine, e
 * quem só rola nunca dispararia `pointerdown`.
 */
const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
] as const;

/**
 * Roda `fn` uma única vez, no primeiro gesto do usuário.
 *
 * Existe para trabalho que só faz sentido para quem está de fato usando a
 * página — gravação de sessão, observability, seções pesadas abaixo da dobra.
 * Adiar para `requestIdleCallback` não bastava: o Lighthouse mede até a página
 * ficar cinco segundos quieta, então trabalho ocioso ainda cai na janela do
 * TBT. No stage, só o LaunchDarkly somava ~500ms de tarefa longa aos 9-11s.
 * Um laboratório não rola nem clica, e para o usuário real a diferença é de
 * frações de segundo.
 *
 * Os ouvintes são passivos e em captura: não atrasam o scroll nem dependem de
 * o evento chegar ao `window` sem ser interrompido. Devolve o cancelamento,
 * para uso direto no cleanup de um efeito.
 */
export function onFirstInteraction(fn: () => void): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  let done = false;
  const options = { capture: true, passive: true } as const;

  const detach = () => {
    for (const type of INTERACTION_EVENTS) {
      window.removeEventListener(type, handle, options);
    }
  };

  function handle() {
    if (done) return;
    done = true;
    detach();
    fn();
  }

  for (const type of INTERACTION_EVENTS) {
    window.addEventListener(type, handle, options);
  }

  return () => {
    done = true;
    detach();
  };
}
